import { useState, useEffect } from 'react';
import {
  FaCalendarAlt, FaCheck, FaTimes, FaClock, FaBan,
  FaChevronDown, FaSearch, FaFilter,
} from 'react-icons/fa';
import api from '../../api';

const STATUS_STYLES = {
  pending:  'text-amber-600 bg-amber-50 border border-amber-200',
  approved: 'text-emerald-600 bg-emerald-50 border border-emerald-200',
  rejected: 'text-red-600 bg-red-50 border border-red-200',
};
const STATUS_ICONS = {
  pending:  <FaClock size={10} />,
  approved: <FaCheck size={10} />,
  rejected: <FaBan size={10} />,
};

function fmt(d) {
  return d
    ? new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC' })
    : '—';
}

function ActionModal({ leave, action, onClose, onDone }) {
  const [note, setNote]         = useState('');
  const [notify, setNotify]     = useState(true);
  const [saving, setSaving]     = useState(false);
  const [error, setError]       = useState('');

  const isApprove = action === 'approve';

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    try {
      const fn = isApprove ? api.approveLeave : api.rejectLeave;
      const updated = await fn(leave.id, { note, notify });
      onDone(updated);
    } catch (err) {
      setError(err.message);
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-6">
        <div className="flex items-center justify-between mb-5">
          <h3 className={`font-bold text-lg ${isApprove ? 'text-emerald-700' : 'text-red-700'}`}>
            {isApprove ? 'Approve Leave Request' : 'Reject Leave Request'}
          </h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><FaTimes size={16} /></button>
        </div>

        <div className="bg-gray-50 rounded-xl p-4 mb-4 text-sm space-y-1">
          <p className="font-semibold text-gray-800">{leave.user_name}</p>
          <p className="text-gray-500">{leave.type} · {leave.days} day{leave.days !== 1 ? 's' : ''}</p>
          <p className="text-gray-500">{fmt(leave.start_date)} – {fmt(leave.end_date)}</p>
          {leave.reason && <p className="text-gray-400 italic">"{leave.reason}"</p>}
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-xs font-semibold text-gray-600 mb-1.5 uppercase tracking-wide">
              {isApprove ? 'Note (optional)' : 'Reason for rejection'}
            </label>
            <textarea
              rows={3}
              value={note}
              onChange={e => setNote(e.target.value)}
              required={!isApprove}
              placeholder={isApprove ? 'Any message for the employee…' : 'Please provide a reason…'}
              className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-blue-300"
            />
          </div>

          <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer select-none">
            <input type="checkbox" checked={notify} onChange={e => setNotify(e.target.checked)}
              className="w-4 h-4 accent-blue-600" />
            Send email notification to employee
          </label>

          {error && <p className="text-red-600 text-sm">{error}</p>}

          <div className="flex justify-end gap-3 pt-1">
            <button type="button" onClick={onClose}
              className="px-4 py-2 text-sm text-gray-600 hover:bg-gray-100 rounded-xl transition-colors">
              Cancel
            </button>
            <button type="submit" disabled={saving}
              className={`px-5 py-2 text-sm text-white font-semibold rounded-xl transition-colors disabled:opacity-50 ${
                isApprove ? 'bg-emerald-600 hover:bg-emerald-700' : 'bg-red-600 hover:bg-red-700'
              }`}>
              {saving ? 'Saving…' : isApprove ? 'Approve' : 'Reject'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default function AdminLeaveRequests() {
  const [leaves, setLeaves]     = useState([]);
  const [loading, setLoading]   = useState(true);
  const [search, setSearch]     = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [modal, setModal]       = useState(null); // { leave, action }

  useEffect(() => {
    api.getLeaves()
      .then(setLeaves)
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const handleDone = (updated) => {
    setLeaves(prev => prev.map(l => l.id === updated.id ? updated : l));
    setModal(null);
  };

  const filtered = leaves.filter(l => {
    const matchSearch = !search ||
      l.user_name?.toLowerCase().includes(search.toLowerCase()) ||
      l.type.toLowerCase().includes(search.toLowerCase()) ||
      (l.user_email && l.user_email.toLowerCase().includes(search.toLowerCase()));
    const matchStatus = filterStatus === 'all' || l.status === filterStatus;
    return matchSearch && matchStatus;
  });

  const counts = {
    all: leaves.length,
    pending:  leaves.filter(l => l.status === 'pending').length,
    approved: leaves.filter(l => l.status === 'approved').length,
    rejected: leaves.filter(l => l.status === 'rejected').length,
  };

  return (
    <div className="bg-slate-50 min-h-screen">

      {modal && (
        <ActionModal
          leave={modal.leave}
          action={modal.action}
          onClose={() => setModal(null)}
          onDone={handleDone}
        />
      )}

      {/* Header */}
      <div className="bg-gradient-to-r from-[#0c1b4d] to-[#1d4ed8] text-white">
        <div className="max-w-6xl mx-auto px-4 py-8">
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <FaCalendarAlt size={20} /> Leave Requests
          </h1>
          <p className="text-blue-200 text-sm mt-1">Review and manage employee time-off requests</p>

          {/* Summary pills */}
          <div className="flex gap-3 mt-5 flex-wrap">
            {[
              { key: 'all',      label: 'All',      color: 'bg-white/20 text-white' },
              { key: 'pending',  label: 'Pending',  color: 'bg-amber-400/30 text-amber-100' },
              { key: 'approved', label: 'Approved', color: 'bg-emerald-400/30 text-emerald-100' },
              { key: 'rejected', label: 'Rejected', color: 'bg-red-400/30 text-red-100' },
            ].map(p => (
              <button key={p.key} onClick={() => setFilterStatus(p.key)}
                className={`px-4 py-1.5 rounded-full text-sm font-semibold transition-all ${p.color} ${filterStatus === p.key ? 'ring-2 ring-white/50' : 'opacity-70 hover:opacity-100'}`}>
                {p.label} · {counts[p.key]}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 py-8 space-y-5">

        {/* Search + filter bar */}
        <div className="flex flex-col sm:flex-row gap-3">
          <div className="relative flex-1">
            <FaSearch size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder="Search by employee or leave type…"
              value={search}
              onChange={e => setSearch(e.target.value)}
              className="w-full pl-9 pr-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-300 bg-white"
            />
          </div>
          <div className="relative">
            <FaFilter size={11} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)}
              className="pl-8 pr-8 py-2.5 border border-gray-200 rounded-xl text-sm bg-white appearance-none focus:outline-none focus:ring-2 focus:ring-blue-300">
              <option value="all">All Statuses</option>
              <option value="pending">Pending</option>
              <option value="approved">Approved</option>
              <option value="rejected">Rejected</option>
            </select>
            <FaChevronDown size={10} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
          </div>
        </div>

        {/* Table */}
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
          {loading && (
            <div className="px-6 py-10 text-center text-gray-400 text-sm">Loading…</div>
          )}
          {!loading && filtered.length === 0 && (
            <div className="px-6 py-10 text-center text-gray-400 text-sm">No leave requests found.</div>
          )}
          {!loading && filtered.length > 0 && (
            <>
              {/* Desktop header */}
              <div className="hidden md:grid grid-cols-12 gap-4 px-6 py-3 bg-gray-50 border-b border-gray-100 text-xs font-semibold text-gray-500 uppercase tracking-wide">
                <div className="col-span-3">Employee</div>
                <div className="col-span-2">Type</div>
                <div className="col-span-3">Dates</div>
                <div className="col-span-1 text-center">Days</div>
                <div className="col-span-1 text-center">Status</div>
                <div className="col-span-2 text-right">Actions</div>
              </div>

              <div className="divide-y divide-gray-50">
                {filtered.map(l => (
                  <div key={l.id} className="px-6 py-4 hover:bg-slate-50 transition-colors">
                    {/* Mobile layout */}
                    <div className="md:hidden space-y-2">
                      <div className="flex items-start justify-between gap-2">
                        <div>
                          <p className="text-sm font-semibold text-gray-800">{l.user_name}</p>
                          <p className="text-xs text-gray-400">{l.user_email}</p>
                        </div>
                        <span className={`inline-flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded-full flex-shrink-0 ${STATUS_STYLES[l.status]}`}>
                          {STATUS_ICONS[l.status]} {l.status}
                        </span>
                      </div>
                      <p className="text-sm text-gray-700">{l.type} · {l.days} day{l.days !== 1 ? 's' : ''}</p>
                      <p className="text-xs text-gray-500">{fmt(l.start_date)} – {fmt(l.end_date)}</p>
                      {l.reason && <p className="text-xs text-gray-400 italic">"{l.reason}"</p>}
                      {l.reviewer_note && <p className="text-xs text-gray-500"><span className="font-medium">Note:</span> {l.reviewer_note}</p>}
                      {l.status === 'pending' && (
                        <div className="flex gap-2 pt-1">
                          <button onClick={() => setModal({ leave: l, action: 'approve' })}
                            className="flex-1 py-1.5 text-xs font-semibold text-emerald-700 bg-emerald-50 hover:bg-emerald-100 rounded-lg transition-colors">
                            Approve
                          </button>
                          <button onClick={() => setModal({ leave: l, action: 'reject' })}
                            className="flex-1 py-1.5 text-xs font-semibold text-red-600 bg-red-50 hover:bg-red-100 rounded-lg transition-colors">
                            Reject
                          </button>
                        </div>
                      )}
                    </div>

                    {/* Desktop layout */}
                    <div className="hidden md:grid grid-cols-12 gap-4 items-center">
                      <div className="col-span-3">
                        <p className="text-sm font-medium text-gray-800">{l.user_name}</p>
                        <p className="text-xs text-gray-400">{l.user_email}</p>
                        {l.employee_id && <p className="text-xs text-gray-400">ID: {l.employee_id}</p>}
                      </div>
                      <div className="col-span-2 text-sm text-gray-700">{l.type}</div>
                      <div className="col-span-3">
                        <p className="text-sm text-gray-700">{fmt(l.start_date)}</p>
                        <p className="text-xs text-gray-400">to {fmt(l.end_date)}</p>
                        {l.reason && <p className="text-xs text-gray-400 italic mt-0.5 truncate max-w-xs">"{l.reason}"</p>}
                      </div>
                      <div className="col-span-1 text-center text-sm font-semibold text-gray-700">{l.days}</div>
                      <div className="col-span-1 flex justify-center">
                        <span className={`inline-flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded-full ${STATUS_STYLES[l.status]}`}>
                          {STATUS_ICONS[l.status]} {l.status}
                        </span>
                      </div>
                      <div className="col-span-2 flex justify-end gap-2">
                        {l.status === 'pending' ? (
                          <>
                            <button onClick={() => setModal({ leave: l, action: 'approve' })}
                              className="px-3 py-1.5 text-xs font-semibold text-emerald-700 bg-emerald-50 hover:bg-emerald-100 rounded-lg transition-colors flex items-center gap-1">
                              <FaCheck size={10} /> Approve
                            </button>
                            <button onClick={() => setModal({ leave: l, action: 'reject' })}
                              className="px-3 py-1.5 text-xs font-semibold text-red-600 bg-red-50 hover:bg-red-100 rounded-lg transition-colors flex items-center gap-1">
                              <FaTimes size={10} /> Reject
                            </button>
                          </>
                        ) : (
                          <span className="text-xs text-gray-400">
                            {l.reviewed_at
                              ? new Date(l.reviewed_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
                              : '—'}
                          </span>
                        )}
                      </div>
                    </div>
                    {l.reviewer_note && l.status !== 'pending' && (
                      <p className="hidden md:block text-xs text-gray-500 mt-1 pl-0">
                        <span className="font-medium">{l.status === 'approved' ? 'Note' : 'Reason'}:</span> {l.reviewer_note}
                      </p>
                    )}
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
